import { useEffect, useMemo, useState } from 'react';

import { rememberApiKey } from '../api.js';
import { CopyableCode } from './CopyableCode.jsx';
import { ScopePicker } from './ScopePicker.jsx';
import { Wizard, WizardStep } from './Wizard.jsx';
import {
  ArrowLeftIcon,
  KeyIcon,
  XIcon,
} from './icons';

/**
 * Shared key-minting UI (card 053 + card 059).
 *
 * Extracted from `routes/ApiKeys.jsx` so the same flow drives three
 * callers:
 *
 *   - `/projects/<slug>/api-keys` — project-scoped admin view, all
 *     four presets visible.  Uses inline mode (embedded card).
 *   - `/keys` — tenant-wide admin view, only system-shaped presets
 *     visible (`presetFilter="system"`).  Uses inline mode.
 *   - `/my-keys` — own-only personal view, only personal-shaped
 *     presets visible (`presetFilter="personal"`), `forceOwnership`
 *     pinned so the client can't accidentally mint a system key from
 *     this surface.  Uses modal mode → renders as a 4-step Wizard.
 *
 * Two render modes:
 *   - `mode="inline"` (default, used by the project + tenant-wide
 *     pages) — renders the legacy two-step preset → form card,
 *     embedded in the page.  No backdrop.  We keep this unchanged
 *     so the project + /keys surfaces stay stable while card 040
 *     iterates on the row sections of ApiKeys.jsx in parallel.
 *   - `mode="modal"` (used by /my-keys) — renders inside the shared
 *     <Wizard> with four discrete steps: preset → name/expiry →
 *     scopes → review/mint.
 *
 * Security note: presetFilter + forceOwnership are UI guards.  The
 * server-side role gate (card 042) is the actual security boundary.
 * The wizard never weakens these — they're funneled through the same
 * `submit()` body the inline form uses.
 */

// Embed (pk) keys are locked to the read-only public SDK surface.
// The scope picker hides everything else when kind === 'pk' so an
// operator can't accidentally mint an admin-able publishable key.
export const EMBED_SCOPES = [
  'tours:read',
  'content:read',
  'themes:read',
  'projects:read',
];

// Authoring set — everything tour / content / theme / pinpoint /
// project authoring needs, no admin:write.  Used as the default
// for personal sk keys subject to role filtering downstream.
const AUTHORING_SCOPES = [
  'tours:read', 'tours:write',
  'content:read', 'content:write',
  'themes:read', 'themes:write',
  'pinpoint:read', 'pinpoint:write',
  'projects:read',
];

// Days a Personal / Bookmarklet key lives by default.  Operators
// override per-mint by setting an explicit expiry.  30 days matches
// the standard "ephemeral developer credential" lifetime on Stripe /
// Vercel / Linear restricted keys and limits the blast radius of a
// bookmark-bar key that's been forgotten about.
const DEFAULT_PERSONAL_EXPIRY_DAYS = 30;

function defaultExpiryFromNow(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  // <input type="date"> wants YYYY-MM-DD — date only, no time. API keys
  // don't need minute precision; presets cover the common cases.
  return d.toISOString().slice(0, 10);
}

// Quick-pick presets for the expiry field (card 073).  "Never" leaves
// the input empty (backend default = no expiry).  Custom date is the
// fallback for anything not on this list.
const EXPIRY_PRESETS = [
  { label: 'Never', days: null },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
];

// Each preset carries `audience: 'personal' | 'system'` so the
// `presetFilter` prop can show only one bucket on each page.  The
// flow itself is unchanged from the card-007 implementation.
const INTENT_PRESETS = [
  {
    id: 'personal',
    audience: 'personal',
    title: 'Personal — your access, anywhere on this tenant',
    body:
      'Mints a sk_live_* key bound to YOUR user — leaving the tenant '
      + 'auto-revokes it.  Scopes inherit from your role (owner / admin '
      + 'gets everything, editor gets authoring, viewer gets read-only).  '
      + 'Default 30-day expiry so a forgotten key doesn\'t live forever.',
    apply: (s) => ({
      ...s,
      kind: 'sk',
      defaultName: 'Personal',
      ownership: 'personal',
      scopeMode: 'role',
      expiryDays: DEFAULT_PERSONAL_EXPIRY_DAYS,
    }),
  },
  {
    id: 'bookmarklet',
    audience: 'personal',
    // Card 048 — stamp `purpose=bookmarklet` so the dashboard's
    // bookmarklet drag-target on /my-keys can filter for these.
    purpose: 'bookmarklet',
    title: 'Use with the bookmarklet',
    body:
      'Mints a pk_live_* key the bookmarklet bundles into its '
      + 'javascript: URL.  The pk by itself does nothing — admin '
      + 'elevation happens at runtime when the SDK confirms you\'re a '
      + 'tenant member via your JWT.  Safe to put on a shared bookmark '
      + 'bar.',
    apply: (s) => ({
      ...s,
      kind: 'pk',
      defaultName: 'Bookmarklet',
      ownership: 'personal',
      scopeMode: 'embed_locked',
      expiryDays: DEFAULT_PERSONAL_EXPIRY_DAYS,
    }),
  },
  {
    id: 'sdk-embed',
    audience: 'system',
    // Card 048 — system-owned pk keys default to `purpose=embed`.
    purpose: 'embed',
    title: 'Embed in your public page (SDK)',
    body:
      'Mints a pk_live_* publishable key locked to the read-only public '
      + 'SDK surface (tours / content / themes / projects).  Safe to '
      + 'paste into your site\'s HTML — leaked from a non-allowed '
      + 'origin it does nothing.  System-owned, survives leavers (the '
      + 'project keeps working after the creator offboards).',
    apply: (s) => ({
      ...s,
      kind: 'pk',
      defaultName: 'Public site embed',
      ownership: 'system',
      scopeMode: 'embed_locked',
      scopes: new Set(EMBED_SCOPES),
    }),
  },
  {
    id: 'mcp',
    audience: 'personal',
    title: 'Use with Claude / Codex / an MCP client',
    body:
      'Mints an sk_live_* key bound to YOUR user with the common MCP '
      + 'scopes — tours / content / themes / pinpoint / projects, read + '
      + 'write.  Offboarding auto-revokes it.  Use with any MCP host '
      + 'that wants to author on the tenant as you.',
    apply: (s) => ({
      ...s,
      kind: 'sk',
      defaultName: 'Claude / Codex',
      ownership: 'personal',
      scopeMode: 'scoped',
      scopes: new Set(AUTHORING_SCOPES),
    }),
  },
];

function intentLabel(id) {
  const p = INTENT_PRESETS.find((x) => x.id === id);
  return p ? p.title : id;
}

/**
 * Public entry point used by /my-keys + /keys.
 *
 * - `mode="inline"` (default) renders the historical preset-card +
 *   form layout (used by the project ApiKeys page directly via
 *   MintKeyForm — see below).
 * - `mode="modal"` opens the wizard surface.
 */
export function MintKeyModal({
  api,
  projectId,
  mode = 'modal',
  presetFilter,    // 'personal' | 'system' | undefined (all)
  forceOwnership,  // 'personal' | 'system' | undefined
  defaultPurpose,  // 'bookmarklet' | 'embed' | undefined (preset wins)
  onCreated,
  onClose,
}) {
  if (mode === 'inline') {
    return (
      <MintKeyForm
        api={api}
        projectId={projectId}
        presetFilter={presetFilter}
        forceOwnership={forceOwnership}
        defaultPurpose={defaultPurpose}
        onCreated={onCreated}
      />
    );
  }
  return (
    <MintKeyWizard
      api={api}
      projectId={projectId}
      presetFilter={presetFilter}
      forceOwnership={forceOwnership}
      defaultPurpose={defaultPurpose}
      onCreated={onCreated}
      onClose={onClose}
    />
  );
}

/* ───────────────────── Wizard (modal mode) ───────────────────── */

/**
 * Four-step mint flow (card 059).
 *
 *   1. Preset — pick an intent card (or "Other / advanced").  The
 *      preset stamps kind, default name, ownership, scopeMode +
 *      scopes, and a default expiry.
 *   2. Name + expiry — operator overrides the preset's default name
 *      and optionally an expiry.
 *   3. Scopes — only meaningful for `scoped` / `role` modes.
 *      `embed_locked` and `full` skip this step automatically via
 *      `optional` + a "Skip" Next button.
 *   4. Review + mint — final confirmation; submit posts to the API.
 *      Plaintext lands on the post-mint state rendered after the
 *      wizard closes; it never lives in sessionStorage (sensitive
 *      draft strip handles this defensively too).
 */
function MintKeyWizard({
  api,
  projectId,
  presetFilter,
  forceOwnership,
  defaultPurpose,
  onCreated,
  onClose,
}) {
  // Caller identity + role on this tenant.  /v1/admin/team flags the
  // calling member as is_self so we can pluck both in one fetch.
  // Used by the Personal preset to stamp owner_user_id and to filter
  // the scope picker to scopes the role is actually allowed to grant.
  const [me, setMe] = useState(null); // { user_id, role }
  useEffect(() => {
    let alive = true;
    api('/v1/admin/team')
      .then((resp) => {
        if (!alive) return;
        const self = (resp?.members || []).find((m) => m.is_self);
        if (self) setMe({ user_id: self.platform_user_id, role: self.role });
      })
      .catch(() => { /* anon view; presets can't stamp owner */ });
    return () => { alive = false; };
  }, [api]);

  // Filtered preset list — only the audience the page allows.  An
  // unset filter falls back to "everything", which is what the
  // project-scoped page wants.
  const visiblePresets = useMemo(() => {
    if (!presetFilter) return INTENT_PRESETS;
    return INTENT_PRESETS.filter((p) => p.audience === presetFilter);
  }, [presetFilter]);

  const [scopeCatalog, setScopeCatalog] = useState(null);
  useEffect(() => {
    let alive = true;
    api('/oauth/scopes')
      .then((c) => { if (alive) setScopeCatalog(c); })
      .catch(() => { /* non-fatal */ });
    return () => { alive = false; };
  }, [api]);

  const [justMinted, setJustMinted] = useState(null);

  if (justMinted) {
    return (
      <div className="mint-modal__backdrop" role="dialog" aria-modal="true" onClick={onClose}>
        <div className="mint-modal" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="mint-modal__close"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            <XIcon size={20} />
          </button>
          <FreshKey minted={justMinted} />
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn--primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
        <style>{MINT_MODAL_CSS}</style>
      </div>
    );
  }

  const initial = {
    intentId: null,
    kind: 'sk',
    scopeMode: 'full',          // 'full' | 'scoped' | 'role' | 'embed_locked'
    selectedScopes: [],         // array (Set is non-JSON-serialisable for sessionStorage)
    ownership: forceOwnership || 'system',
    purpose: defaultPurpose || '',
    name: '',
    namePlaceholder: 'Production embed',
    expiresAt: '',
  };

  function applyPreset(preset, base) {
    const next = preset.apply({
      kind: 'sk',
      scopeMode: 'scoped',
      scopes: new Set(),
      ownership: 'system',
      defaultName: '',
      expiryDays: 0,
    });
    return {
      ...base,
      intentId: preset.id,
      kind: next.kind,
      ownership: forceOwnership || next.ownership || 'system',
      name: next.defaultName || base.name,
      namePlaceholder: next.defaultName || base.namePlaceholder,
      scopeMode: next.scopeMode,
      selectedScopes: next.scopes ? Array.from(next.scopes) : [],
      expiresAt: next.expiryDays ? defaultExpiryFromNow(next.expiryDays) : '',
      purpose: preset.purpose || defaultPurpose || '',
    };
  }

  return (
    <Wizard
      id="mint-api-key"
      title="Mint a new API key"
      subtitle="Pick a preset, name it, confirm the scopes, then we'll generate the secret."
      initialValues={initial}
      submitLabel="Mint key"
      onCancel={onClose}
      onSubmit={async (values) => {
        const body = {
          name: (values.name || '').trim(),
          kind: values.kind,
          project_id: projectId,
        };
        if (values.expiresAt) {
          body.expires_at = new Date(values.expiresAt).toISOString();
        }
        if (values.scopeMode === 'embed_locked') {
          body.scopes = [...EMBED_SCOPES];
        } else if (values.scopeMode === 'scoped' || values.scopeMode === 'role') {
          body.scopes = values.selectedScopes || [];
          if (body.scopes.length === 0) {
            throw new Error('Pick at least one scope.');
          }
        }
        const effectiveOwnership = forceOwnership || values.ownership;
        if (effectiveOwnership === 'personal') {
          if (!me?.user_id) {
            throw new Error("Couldn't read your tenant identity — refresh and try again.");
          }
          body.owner_user_id = me.user_id;
        }
        if (values.purpose) body.purpose = values.purpose;

        const created = await api('/v1/admin/api-keys', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (created?.prefix && created?.key) {
          rememberApiKey(created.prefix, created.key);
        }
        setJustMinted(created);
        onCreated?.(created);
      }}
    >
      <WizardStep
        title="Choose a preset"
        isValid={(v) => !!v.intentId}
      >
        {({ values, setMany }) => (
          <div className="wiz-stack">
            <p className="wiz-hint" style={{ margin: 0 }}>
              {presetFilter === 'personal' && (
                <>These presets stamp YOU as the owner — leaving the tenant auto-revokes the key.</>
              )}
              {presetFilter === 'system' && (
                <>System-owned keys survive whoever introduced them — use these for project embeds and shared backend integrations.</>
              )}
              {!presetFilter && (
                <>What do you want to do with this key? Pick a preset to pre-fill kind, scopes, and a default expiry.</>
              )}
            </p>
            <div className="intent-grid">
              {visiblePresets.map((p) => {
                const active = values.intentId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`intent-card${active ? ' intent-card--active' : ''}`}
                    onClick={() => setMany(applyPreset(p, values))}
                    aria-pressed={active}
                  >
                    <div className="intent-card__title">{p.title}</div>
                    <div className="intent-card__body">{p.body}</div>
                  </button>
                );
              })}
              <button
                type="button"
                className={`intent-card intent-card--advanced${values.intentId === 'advanced' ? ' intent-card--active' : ''}`}
                onClick={() => setMany({
                  ...values,
                  intentId: 'advanced',
                  ownership: forceOwnership || 'system',
                  purpose: defaultPurpose || '',
                  scopeMode: 'full',
                  selectedScopes: [],
                })}
                aria-pressed={values.intentId === 'advanced'}
              >
                <div className="intent-card__title">Other / advanced</div>
                <div className="intent-card__body">
                  Open the full form with every knob.  Use when you want a
                  non-default scope set or a key for a use case not listed
                  above.
                </div>
              </button>
            </div>
            <style>{INTENT_CSS}</style>
          </div>
        )}
      </WizardStep>

      <WizardStep
        title="Name + expiry"
        isValid={(v) => true /* name has a placeholder fallback */}
      >
        {({ values, set }) => (
          <div className="wiz-stack">
            <label className="field">
              <span className="field__label">Name</span>
              <input
                className="input"
                value={values.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder={values.namePlaceholder}
              />
              <p className="wiz-hint">Internal label shown next to the prefix in the keys list.</p>
            </label>
            <label className="field">
              <span className="field__label">Key type</span>
              <select
                className="select"
                value={values.kind}
                onChange={(e) => set('kind', e.target.value)}
                disabled={values.intentId !== 'advanced'}
              >
                <option value="sk">Secret (sk_) — server-side, full admin</option>
                <option value="pk">Publishable (pk_) — safe to embed in page source</option>
              </select>
              <p className="wiz-hint">
                {values.intentId === 'advanced'
                  ? 'Pick sk_ for backend integrations, pk_ for browser-embeddable surfaces.'
                  : 'Set by the preset — switch to "Other / advanced" on step 1 to override.'}
              </p>
            </label>
            <label className="field">
              <span className="field__label">Expires (optional)</span>
              <ExpiryField
                value={values.expiresAt}
                onChange={(v) => set('expiresAt', v)}
              />
              <p className="wiz-hint">
                Personal-style presets default to 30 days.  System
                presets default to "no expiry" — system keys
                shouldn't silently disappear.
              </p>
            </label>
          </div>
        )}
      </WizardStep>

      <WizardStep
        title="Scopes"
        optional
        isValid={(v) => {
          // Skippable when the preset locks scopes (embed_locked) or
          // explicitly grants full access.  scoped / role modes need
          // at least one checkbox.
          if (v.scopeMode === 'embed_locked' || v.scopeMode === 'full') return true;
          return (v.selectedScopes || []).length > 0;
        }}
      >
        {({ values, set, setMany }) => (
          <ScopesStep
            values={values}
            set={set}
            setMany={setMany}
            scopeCatalog={scopeCatalog}
            me={me}
          />
        )}
      </WizardStep>

      <WizardStep title="Review">
        {({ values }) => {
          const effectiveOwnership = forceOwnership || values.ownership;
          return (
            <div className="wiz-stack">
              <p className="wiz-hint" style={{ margin: 0 }}>
                We&apos;ll mint the key and show you the plaintext ONCE
                after this step. Save it to your password manager before
                closing.
              </p>
              <dl className="wiz-review">
                <dt>Preset</dt>
                <dd>{values.intentId === 'advanced' ? 'Other / advanced' : intentLabel(values.intentId)}</dd>
                <dt>Name</dt>
                <dd>{values.name || <em>(using placeholder &ldquo;{values.namePlaceholder}&rdquo;)</em>}</dd>
                <dt>Kind</dt>
                <dd>{values.kind === 'pk' ? 'pk_ — publishable' : 'sk_ — secret'}</dd>
                <dt>Ownership</dt>
                <dd>
                  {effectiveOwnership === 'personal'
                    ? <>personal · bound to your user · auto-revokes on offboarding</>
                    : <>system-owned · survives leavers</>}
                </dd>
                <dt>Scopes</dt>
                <dd>
                  {values.scopeMode === 'full' && 'Full access (all scopes)'}
                  {values.scopeMode === 'embed_locked' && `Locked to read-only SDK surface (${EMBED_SCOPES.join(', ')})`}
                  {(values.scopeMode === 'scoped' || values.scopeMode === 'role') && (
                    (values.selectedScopes || []).length > 0
                      ? (values.selectedScopes || []).join(', ')
                      : <em>none picked — go back to step 3</em>
                  )}
                </dd>
                <dt>Expires</dt>
                <dd>{values.expiresAt ? new Date(values.expiresAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : <em>never</em>}</dd>
                {values.purpose && (
                  <>
                    <dt>Purpose tag</dt>
                    <dd><code>{values.purpose}</code></dd>
                  </>
                )}
              </dl>
            </div>
          );
        }}
      </WizardStep>
    </Wizard>
  );
}

function ScopesStep({ values, set, setMany, scopeCatalog, me }) {
  // Card 080 — Personal preset stamps `scopeMode: 'role'` on step 1
  // but can't pre-fill the scope set at that point (catalogue +
  // me.role hadn't loaded yet).  Without a seed the user lands here
  // with no checkboxes ticked, the Next button stays disabled, and
  // the wizard appears stuck.  The legacy inline form has the same
  // auto-seed effect (`MintKeyForm` lines ~783).  Mirror the rule:
  // when both the catalogue and the caller's role are available, AND
  // selectedScopes is empty, AND we're in role mode, seed from
  // `role_available[me.role]`.  Operators with higher roles still
  // see all scopes ticked; they can uncheck to narrow further.
  useEffect(() => {
    if (values.scopeMode !== 'role') return;
    if (!scopeCatalog || !me?.role) return;
    if ((values.selectedScopes || []).length > 0) return;
    const allowed = scopeCatalog?.role_available?.[me.role] || [];
    if (allowed.length === 0) return;
    set('selectedScopes', [...allowed]);
  }, [
    values.scopeMode,
    values.selectedScopes,
    scopeCatalog,
    me?.role,
    set,
  ]);

  if (values.scopeMode === 'embed_locked') {
    return (
      <div className="wiz-stack">
        <p className="wiz-hint" style={{ margin: 0 }}>
          Publishable embed keys are locked to the read-only public
          SDK surface — the picker is intentionally disabled here so a
          leaked key can never reach admin endpoints.
        </p>
        <div className="scopes-grid">
          {EMBED_SCOPES.map((scope) => {
            const meta = (scopeCatalog?.scopes || []).find((s) => s.scope === scope);
            return (
              <label key={scope} className="scope-row" style={{ opacity: 0.85 }}>
                <input type="checkbox" checked readOnly disabled />
                <span>
                  <strong>{meta?.label || scope}</strong>{' '}
                  <code style={{ fontSize: 11, color: 'var(--text-3)' }}>{scope}</code>
                </span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }
  const visibleScopes = (() => {
    if (!scopeCatalog) return [];
    const all = scopeCatalog.scopes || [];
    if (values.kind === 'pk') {
      return all.filter((s) => EMBED_SCOPES.includes(s.scope));
    }
    if (values.scopeMode === 'role' && me) {
      const allowed = new Set(scopeCatalog.role_available?.[me.role] || []);
      return all.filter((s) => allowed.has(s.scope));
    }
    return all;
  })();
  const selected = new Set(values.selectedScopes || []);
  function toggleScope(s) {
    const next = new Set(selected);
    if (next.has(s)) next.delete(s); else next.add(s);
    set('selectedScopes', Array.from(next));
  }
  return (
    <div className="wiz-stack">
      <div className="row" style={{ gap: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="radio"
            name="scope-mode"
            value="full"
            checked={values.scopeMode === 'full'}
            onChange={() => setMany({ ...values, scopeMode: 'full' })}
          />
          Full access
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="radio"
            name="scope-mode"
            value="scoped"
            checked={values.scopeMode === 'scoped' || values.scopeMode === 'role'}
            onChange={() => setMany({ ...values, scopeMode: 'scoped' })}
          />
          Pick specific scopes
        </label>
      </div>
      {values.scopeMode === 'full' && (
        <p className="wiz-hint" style={{ margin: 0 }}>
          Equivalent to every scope below. Use for trusted server-to-
          server integrations where you don&apos;t want to maintain a
          scope set.  Skip to continue.
        </p>
      )}
      {values.scopeMode === 'role' && me?.role && (
        <p className="wiz-hint" style={{ margin: 0 }}>
          Picker is narrowed to what your role ({me.role}) can grant.
          Pick fewer to narrow this key further.
        </p>
      )}
      {(values.scopeMode === 'scoped' || values.scopeMode === 'role') && (
        <div className="scopes-grid">
          {visibleScopes.map((s) => (
            <label key={s.scope} className="scope-row">
              <input
                type="checkbox"
                checked={selected.has(s.scope)}
                onChange={() => toggleScope(s.scope)}
              />
              <span>
                <strong>{s.label}</strong>{' '}
                <code style={{ fontSize: 11, color: 'var(--text-3)' }}>{s.scope}</code>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{s.description}</div>
              </span>
            </label>
          ))}
          {!scopeCatalog && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading scopes…</div>
          )}
        </div>
      )}
      <style>{SCOPES_CSS}</style>
    </div>
  );
}

/* ───────────────────── Legacy inline form ─────────────────────
 *
 * The pre-wizard surface, kept verbatim for /projects/<slug>/api-keys
 * and the /keys tenant-wide page where the form lives embedded in a
 * card.  Card 040 is iterating on the row sections of ApiKeys.jsx in
 * parallel — leaving this untouched avoids stepping on its toes.
 */

export function MintKeyForm({
  api,
  projectId,
  presetFilter,
  forceOwnership,
  defaultPurpose,
  onCreated,
}) {
  const [step, setStep] = useState('intent'); // 'intent' | 'form'
  const [intentId, setIntentId] = useState(null);
  const [namePlaceholder, setNamePlaceholder] = useState('Production embed');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('sk');
  // 'full'         → no scope set (legacy/advanced only)
  // 'scoped'       → operator picks; subject to role filtering
  // 'role'         → personal preset; defaults to the user's role-
  //                  available scopes, picker is editable but the
  //                  catalogue is filtered to what the role can grant
  // 'embed_locked' → pk preset; locked to EMBED_SCOPES, picker hidden
  const [scopeMode, setScopeMode] = useState('full');
  const [selectedScopes, setSelectedScopes] = useState(new Set());
  const [scopeCatalog, setScopeCatalog] = useState(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [ownership, setOwnership] = useState(forceOwnership || 'system');
  const [purpose, setPurpose] = useState(defaultPurpose || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [justMinted, setJustMinted] = useState(null);

  const [me, setMe] = useState(null);
  useEffect(() => {
    let alive = true;
    api('/v1/admin/team')
      .then((resp) => {
        if (!alive) return;
        const self = (resp?.members || []).find((m) => m.is_self);
        if (self) setMe({ user_id: self.platform_user_id, role: self.role });
      })
      .catch(() => { /* anon view */ });
    return () => { alive = false; };
  }, [api]);

  const visiblePresets = useMemo(() => {
    if (!presetFilter) return INTENT_PRESETS;
    return INTENT_PRESETS.filter((p) => p.audience === presetFilter);
  }, [presetFilter]);

  function pickIntent(preset) {
    const next = preset.apply({
      kind: 'sk',
      scopeMode: 'scoped',
      scopes: new Set(),
      ownership: 'system',
      defaultName: '',
      expiryDays: 0,
    });
    setIntentId(preset.id);
    setKind(next.kind);
    setOwnership(forceOwnership || next.ownership || 'system');
    if (next.defaultName) {
      setName(next.defaultName);
      setNamePlaceholder(next.defaultName);
    }
    setScopeMode(next.scopeMode);
    setSelectedScopes(next.scopes || new Set());
    setExpiresAt(next.expiryDays ? defaultExpiryFromNow(next.expiryDays) : '');
    setPurpose(preset.purpose || defaultPurpose || '');
    setStep('form');
  }

  function pickAdvanced() {
    setIntentId('advanced');
    setOwnership(forceOwnership || 'system');
    setPurpose(defaultPurpose || '');
    setStep('form');
  }

  function backToIntent() {
    setStep('intent');
    setIntentId(null);
    setName('');
    setExpiresAt('');
    setErr(null);
    setOwnership(forceOwnership || 'system');
    setPurpose(defaultPurpose || '');
  }

  const needsCatalog = scopeMode === 'scoped' || scopeMode === 'role';
  useEffect(() => {
    if (!needsCatalog || scopeCatalog) return;
    let alive = true;
    api('/oauth/scopes')
      .then((c) => { if (alive) setScopeCatalog(c); })
      .catch(() => { /* falls back to manual */ });
    return () => { alive = false; };
  }, [needsCatalog, scopeCatalog, api]);

  useEffect(() => {
    if (scopeMode !== 'role') return;
    if (!scopeCatalog || !me) return;
    const allowed = scopeCatalog?.role_available?.[me.role] || [];
    if (selectedScopes.size === 0 && allowed.length > 0) {
      setSelectedScopes(new Set(allowed));
    }
  }, [scopeMode, scopeCatalog, me, selectedScopes.size]);

  const visibleScopes = useMemo(() => {
    if (!scopeCatalog) return [];
    const all = scopeCatalog.scopes || [];
    if (kind === 'pk') {
      return all.filter((s) => EMBED_SCOPES.includes(s.scope));
    }
    if (scopeMode === 'role' && me) {
      const allowed = new Set(scopeCatalog.role_available?.[me.role] || []);
      return all.filter((s) => allowed.has(s.scope));
    }
    return all;
  }, [scopeCatalog, kind, scopeMode, me]);

  // Card 040: locked embed preset — render the four EMBED_SCOPES
  // even before the catalogue has loaded so the row layout never
  // flashes empty.  Once the catalogue arrives we swap in the real
  // labels + descriptions.
  const embedScopeRows = useMemo(() => {
    const catalog = scopeCatalog?.scopes || [];
    return EMBED_SCOPES.map((scope) => {
      const meta = catalog.find((s) => s.scope === scope);
      return meta || { scope, label: scope, description: '' };
    });
  }, [scopeCatalog]);

  function toggleScope(s) {
    if (scopeMode === 'embed_locked') return;
    const next = new Set(selectedScopes);
    if (next.has(s)) next.delete(s); else next.add(s);
    setSelectedScopes(next);
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setJustMinted(null);
    try {
      const body = {
        name: name.trim(),
        kind,
        project_id: projectId,
      };
      if (expiresAt) {
        body.expires_at = new Date(expiresAt).toISOString();
      }
      if (scopeMode === 'embed_locked') {
        body.scopes = [...EMBED_SCOPES];
      } else if (scopeMode === 'scoped' || scopeMode === 'role') {
        body.scopes = Array.from(selectedScopes);
        if (body.scopes.length === 0) {
          throw new Error('Pick at least one scope.');
        }
      }
      const effectiveOwnership = forceOwnership || ownership;
      if (effectiveOwnership === 'personal') {
        if (!me?.user_id) {
          throw new Error("Couldn't read your tenant identity — refresh and try again.");
        }
        body.owner_user_id = me.user_id;
      }
      if (purpose) {
        body.purpose = purpose;
      }
      const created = await api('/v1/admin/api-keys', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setName('');
      if (created?.prefix && created?.key) {
        rememberApiKey(created.prefix, created.key);
      }
      setJustMinted(created);
      onCreated?.(created);
      setStep('intent');
      setIntentId(null);
      setOwnership(forceOwnership || 'system');
      setPurpose(defaultPurpose || '');
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  if (step === 'intent') {
    return (
      <section className="card">
        <h3 className="card__title">Mint a new key</h3>
        <p style={{ color: 'var(--text-3)', fontSize: 13, margin: '6px 0 12px' }}>
          {presetFilter === 'personal' && (
            <>
              These presets all stamp YOU as the owner — leaving the
              tenant auto-revokes them. Pick one and we&apos;ll pre-fill
              kind, scopes, and a 30-day expiry; you can still tweak
              before minting.
            </>
          )}
          {presetFilter === 'system' && (
            <>
              System-owned keys survive whoever introduced them — use
              these for project embeds and shared backend integrations.
              Pick a preset to pre-fill the right shape.
            </>
          )}
          {!presetFilter && (
            <>
              What do you want to do with this key? Pick an option and
              we&apos;ll pre-fill the right shape — kind, scopes, name. You
              can still tweak everything before minting.
            </>
          )}
        </p>
        <div className="intent-grid">
          {visiblePresets.map((p) => (
            <button key={p.id} type="button" className="intent-card" onClick={() => pickIntent(p)}>
              <div className="intent-card__title">{p.title}</div>
              <div className="intent-card__body">{p.body}</div>
            </button>
          ))}
          <button type="button" className="intent-card intent-card--advanced" onClick={pickAdvanced}>
            <div className="intent-card__title">Other / advanced</div>
            <div className="intent-card__body">
              Open the full form with every knob. Use when you want a
              non-default scope set or a key for a use case not listed
              above.
            </div>
          </button>
        </div>
        {justMinted && <FreshKey minted={justMinted} />}
        <style>{INTENT_CSS}</style>
      </section>
    );
  }

  return (
    <section className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--ghost" onClick={backToIntent} aria-label="Back" title="Back to preset picker">
          <ArrowLeftIcon /> Back
        </button>
        <h3 className="card__title" style={{ margin: 0 }}>Mint a new key — {intentId === 'advanced' ? 'advanced' : intentLabel(intentId)}</h3>
        {ownership === 'personal' && me?.user_id && (
          <span
            className="badge badge--editor"
            title={`Bound to your user (${me.user_id}). Offboarding revokes it.`}
          >
            personal · yours
          </span>
        )}
        {ownership === 'system' && (
          <span
            className="badge badge--default"
            title="System-owned. Survives leavers."
          >
            system-owned
          </span>
        )}
        {purpose && (
          <span
            className="badge badge--default"
            title={`Stamped purpose=${purpose} — drives where this key shows up in the dashboard.`}
          >
            purpose · {purpose}
          </span>
        )}
      </div>
      <form onSubmit={submit} className="mint-form">
        <div className="mint-form__row mint-form__row--primary">
          <label className="field">
            <span className="field__label">Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={namePlaceholder}
            />
          </label>
          <label className="field">
            <span className="field__label">Key type</span>
            <select
              className="input"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="sk">Secret (sk_) — server-side, full admin</option>
              <option value="pk">Publishable (pk_) — safe to embed in page source</option>
            </select>
          </label>
          <button
            type="submit"
            data-ui-name="mint-key-button"
            disabled={busy}
            className="btn btn--primary mint-form__submit"
          >
            <KeyIcon /> {busy ? 'Minting…' : 'Mint key'}
          </button>
        </div>
        <ExpiryField value={expiresAt} onChange={setExpiresAt} />
      </form>
      <style>{MINT_FORM_CSS}</style>

      <fieldset style={{ marginTop: 14, border: '1px solid var(--border-1)', borderRadius: 10, padding: '10px 12px' }}>
        <legend style={{ fontSize: 12, fontWeight: 600, padding: '0 6px', color: 'var(--text-3)' }}>Scopes</legend>

        {scopeMode === 'embed_locked' ? (
          <>
            <p style={{ color: 'var(--text-3)', fontSize: 12.5, margin: '0 0 8px' }}>
              Publishable embed keys are locked to the read-only
              public SDK surface — the picker is intentionally
              disabled here so a leaked key can never reach admin
              endpoints.
            </p>
            <ScopePicker
              scopes={embedScopeRows}
              selected={new Set(EMBED_SCOPES)}
              onToggle={() => {}}
              loading={!scopeCatalog}
              disabled
              showHint={false}
              idPrefix="mint-embed"
            />
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="radio"
                  name="scope-mode"
                  value="full"
                  checked={scopeMode === 'full'}
                  onChange={() => setScopeMode('full')}
                />
                Full access
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="radio"
                  name="scope-mode"
                  value="scoped"
                  checked={scopeMode === 'scoped' || scopeMode === 'role'}
                  onChange={() => setScopeMode('scoped')}
                />
                Pick specific scopes
              </label>
            </div>
            {scopeMode === 'full' && (
              <p style={{ color: 'var(--text-3)', fontSize: 12, margin: 0 }}>
                Equivalent to every scope below. Use for trusted server-to-server
                integrations where you don&apos;t want to maintain a scope set.
              </p>
            )}
            {scopeMode === 'role' && me?.role && (
              <p style={{ color: 'var(--text-3)', fontSize: 12, margin: '0 0 6px' }}>
                Picker is narrowed to what your role ({me.role}) can grant.
                Operators with higher roles can mint keys carrying more
                scopes; pick fewer to narrow this one further.
              </p>
            )}
            {(scopeMode === 'scoped' || scopeMode === 'role') && (
              <ScopePicker
                scopes={visibleScopes}
                selected={selectedScopes}
                onToggle={toggleScope}
                loading={!scopeCatalog}
                idPrefix="mint-scoped"
              />
            )}
          </>
        )}
      </fieldset>

      <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: 10 }}>
        Use <strong>pk_</strong> for the SDK <code>data-api-key</code>
        attribute on your site — leaked from a non-allowed origin it
        does nothing. Use <strong>sk_</strong> only from servers or
        trusted CLI tools.
      </p>
      {err && <p style={{ color: 'var(--danger-700)', marginTop: 12 }}>{err}</p>}
      {justMinted && <FreshKey minted={justMinted} />}
    </section>
  );
}

export function FreshKey({ minted }) {
  // Defensive: if the server response didn't include the plaintext
  // (regression in the backend, envelope-middleware misroute, etc.)
  // surface that loudly instead of rendering an invisible empty
  // <code> block — the historic bug was a silently blank field
  // that left users wondering why nothing copied.
  if (!minted?.key) {
    return (
      <div className="state state--error" style={{ marginTop: 16, textAlign: 'left' }}>
        <div className="state__title">
          Key was created (prefix <code>{minted?.prefix || 'unknown'}</code>) but
          the server did not return the plaintext.
        </div>
        <div className="list__meta" style={{ marginTop: 8 }}>
          Revoke this key and mint another. If the problem repeats,
          check the API logs around <code>admin.api_key.created</code>.
        </div>
      </div>
    );
  }
  return (
    <div className="state" style={{ marginTop: 16, textAlign: 'left' }}>
      <div className="state__title">Save this key now — it won&apos;t be shown again</div>
      <div style={{ marginTop: 8 }}>
        <CopyableCode value={minted.key} label="key" />
      </div>
      <div className="list__meta" style={{ marginTop: 8 }}>
        Prefix <code>{minted.prefix}</code>
        {' · '}
        {minted.project_id ? 'project-scoped' : 'tenant-wide'}
        {' · '}
        <span style={{ color: 'var(--danger-700)' }}>
          store in your secrets manager; we only keep the hash.
        </span>
      </div>
    </div>
  );
}

/**
 * Expiry picker for the mint form (card 073).  Replaces the native
 * datetime-local input — API keys don't need minute precision.  A row
 * of presets does the common cases (Never / 30 days / 90 days / 1
 * year); "Custom date" reveals a date-only input for everything else.
 *
 * Stores YYYY-MM-DD on the parent state (or '' for Never); the submit
 * helper converts to ISO via new Date(YYYY-MM-DD).toISOString().
 */
function ExpiryField({ value, onChange }) {
  const matchedPreset = useMemo(() => {
    if (!value) return 'never';
    for (const p of EXPIRY_PRESETS) {
      if (p.days != null && defaultExpiryFromNow(p.days) === value) {
        return p.label;
      }
    }
    return 'custom';
  }, [value]);

  return (
    <div className="expiry-field">
      <div className="expiry-field__presets" role="radiogroup" aria-label="Expiry preset">
        {EXPIRY_PRESETS.map((p) => {
          const active = matchedPreset === p.label;
          return (
            <button
              key={p.label}
              type="button"
              role="radio"
              aria-checked={active}
              className={`expiry-field__chip${active ? ' expiry-field__chip--active' : ''}`}
              onClick={() => onChange(p.days == null ? '' : defaultExpiryFromNow(p.days))}
            >
              {p.label}
            </button>
          );
        })}
        <button
          type="button"
          role="radio"
          aria-checked={matchedPreset === 'custom'}
          className={`expiry-field__chip${matchedPreset === 'custom' ? ' expiry-field__chip--active' : ''}`}
          onClick={() => {
            if (matchedPreset !== 'custom') {
              onChange(defaultExpiryFromNow(60));
            }
          }}
        >
          Custom
        </button>
      </div>
      {matchedPreset === 'custom' && (
        <input
          type="date"
          className="input expiry-field__date"
          value={value}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

const MINT_FORM_CSS = `
  .mint-form { display: flex; flex-direction: column; gap: 10px; margin-top: 10px; }
  .mint-form__row--primary {
    display: grid;
    grid-template-columns: 2fr 1.6fr auto;
    gap: 12px;
    align-items: end;
  }
  .mint-form__submit { height: 36px; }
  @media (max-width: 720px) {
    .mint-form__row--primary { grid-template-columns: 1fr; }
    .mint-form__submit { width: 100%; justify-content: center; }
  }
  .expiry-field { display: flex; flex-direction: column; gap: 6px; }
  .expiry-field__presets { display: flex; flex-wrap: wrap; gap: 6px; }
  .expiry-field__chip {
    padding: 5px 11px;
    border: 1px solid var(--border-1);
    background: var(--surface-0);
    border-radius: 999px;
    font-size: 12px;
    color: var(--text-2);
    cursor: pointer;
    line-height: 1.2;
  }
  .expiry-field__chip:hover { background: var(--surface-1); color: var(--text-1); }
  .expiry-field__chip--active {
    background: var(--brand-50, #eef2ff);
    border-color: var(--brand-600, #4f46e5);
    color: var(--brand-700, #3730a3);
    font-weight: 500;
  }
  .expiry-field__date { max-width: 200px; }
`;

const INTENT_CSS = `
  .intent-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 10px; }
  .intent-card { text-align: left; padding: 14px; background: var(--surface-0); border: 1px solid var(--border-1); border-radius: 10px; cursor: pointer; font: inherit; color: inherit; display: flex; flex-direction: column; gap: 6px; transition: transform .12s ease, border-color .12s ease, box-shadow .12s ease; }
  .intent-card:hover { transform: translateY(-1px); border-color: var(--brand-600, #4f46e5); box-shadow: 0 10px 24px -16px var(--brand-600, #4f46e5); }
  .intent-card--active { border-color: var(--brand-600, #4f46e5); box-shadow: 0 0 0 2px var(--brand-100, #e0e7ff); background: var(--brand-50, #eef2ff); }
  .intent-card--advanced { background: var(--surface-1); border-style: dashed; }
  .intent-card__title { font-weight: 600; font-size: 14px; color: var(--text-1, #0f172a); }
  .intent-card__body { font-size: 12.5px; color: var(--text-3, #64748b); line-height: 1.4; }
`;

const MINT_MODAL_CSS = `
  .mint-modal__backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15,23,42,0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
    overflow: auto;
  }
  .mint-modal {
    position: relative;
    background: var(--surface-0);
    border-radius: 12px;
    width: 100%;
    max-width: 640px;
    max-height: calc(100vh - 32px);
    overflow: auto;
    box-shadow: 0 24px 48px -16px rgba(15,23,42,0.4);
    padding: 16px;
  }
  .mint-modal__close {
    position: absolute;
    top: 10px;
    right: 10px;
    background: transparent;
    border: 0;
    cursor: pointer;
    color: var(--text-3);
    padding: 4px;
    border-radius: 6px;
    z-index: 1;
  }
  .mint-modal__close:hover {
    background: var(--surface-1);
    color: var(--text-1);
  }
`;
